/**
 * Utility functions for address and location formatting
 */

/**
 * Validates if a string is a valid Canadian postal code
 * Format: A1A 1A1 or A1A-1A1
 * @param value The postal code string to validate
 * @returns True if the value is a valid Canadian postal code
 */
export function isPostalCode(value: string): boolean {
  if (!value) return false;
  const trimmed = value.trim();

  const regex = /^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/;
  return regex.test(trimmed);
}

/**
 * Validates if a string is a Canadian province code or name
 * Supports both province codes (AB, BC, etc.) and full names
 * Normalizes input by lowercasing, removing accents, and trimming spaces
 * @param value The province code or name to validate
 * @returns True if the value is a valid Canadian province
 */
export function isCanadianProvince(value: string): boolean {
  if (!value) return false;

  // Normalize string: lowercase, remove accents, trim spaces
  const normalize = (str: string) =>
    str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .trim();

  const input = normalize(value);

  const provinceCodes = [
    'ab',
    'bc',
    'mb',
    'nb',
    'nl',
    'ns',
    'nt',
    'nu',
    'on',
    'pe',
    'qc',
    'sk',
    'yt'
  ];

  const provinceNames = [
    'alberta',
    'british columbia',
    'manitoba',
    'new brunswick',
    'newfoundland and labrador',
    'nova scotia',
    'northwest territories',
    'nunavut',
    'ontario',
    'prince edward island',
    'quebec',
    'saskatchewan',
    'yukon'
  ];

  return provinceCodes.includes(input) || provinceNames.includes(input);
}

/**
 * Formats a complete address for display as a title
 * Splits the address by commas and reformats it with line breaks
 * @param adresseImmeuble The complete address string
 * @returns The formatted title address
 */
export function formatAddressTitle(adresseImmeuble: string): string {
  if (!adresseImmeuble) return '';

  let partis = adresseImmeuble.split(',').map((p) => p.trim());

  if (partis.length >= 6) {
    return `${partis[0]}, ${partis[1]}, ${partis[2]}\n${partis[3]} (${partis[4]}) ${partis.slice(5).join(' ')}`;
  } else if (partis.length === 5) {
    return `${partis[0]}, ${partis[1]}\n${partis[2]} (${partis[3]}) ${partis[4]}`;
  } else if (partis.length === 4) {
    if (isCanadianProvince(partis[3])) {
      return `${partis[0]}, ${partis[1]}\n${partis[2]} (${partis[3]})`;
    } else if (isCanadianProvince(partis[2])) {
      return `${partis[0]}\n${partis[1]} (${partis[2]}) ${partis[3]}`;
    } else {
      // no province
      return `${partis[0]}, ${partis[1]}\n${partis[2]} ${partis[3]}`;
    }
  } else if (partis.length === 3) {
    return `${partis[0]}\n${partis[1]} (${partis[2]})`;
  }

  return adresseImmeuble; // Returns original if it doesn't follow expected format
}
